/**
 * Viewer-mode helper shared across game browser sources.
 *
 * Two distinct WS endpoints feed game browser SPAs:
 *
 *   1. Streamer / OBS (read-write):
 *        wss://api.thegamer.network/browser?key=<API_KEY>
 *      The full /browser handshake (hello + heartbeat ping/pong + clock-sync).
 *      Auth via the per-tenant API key.
 *
 *   2. Public watch-page viewer iframe (read-only):
 *        wss://api.thegamer.network/watch/<HANDLE>/feed
 *      Server hard-closes (4429) on any inbound frame. Client must NOT send
 *      `hello`, `ping`, or anything else. Same OutMsg wire format as /browser
 *      so reducer code can be shared.
 *
 * The watch SPA loads each game iframe at:
 *   https://<slug>.thegamer.network/?viewer=1&handle=<HANDLE>
 *
 * This module exposes the helpers each game's `main.ts` uses to:
 *   - parse `viewer=1` + `handle` from the URL
 *   - construct the right WS URL (prod via wss://api.<apex>, dev via
 *     ws://<hostname>:<wsPort>)
 *   - signal the WS client that no client→server frames may be emitted
 *
 * Streamer mode (the existing `?key=...` path) is untouched — `readViewerMode`
 * returns `kind: 'streamer'` whenever `viewer=1` is absent, even if the handle
 * is blank, so existing OBS browser-source URLs keep working unchanged.
 *
 * @packageDocumentation
 */

/** Parsed viewer-mode signal from the iframe URL params. */
export type ViewerMode =
  | { kind: 'streamer'; key: string }
  | { kind: 'viewer'; handle: string };

/**
 * Parse `viewer` + `handle` (and the legacy `key`) from the iframe URL.
 *
 * Returns `kind: 'viewer'` only when BOTH `viewer=1` AND a non-empty `handle`
 * are present — otherwise falls back to streamer mode using the existing `key`
 * query param (default: `dev-no-key` to preserve current dev behaviour).
 */
export function readViewerMode(params: URLSearchParams): ViewerMode {
  const isViewer = params.get('viewer') === '1';
  const handle = (params.get('handle') ?? '').trim();
  if (isViewer && handle.length > 0) {
    return { kind: 'viewer', handle };
  }
  const key = params.get('key') ?? 'dev-no-key';
  return { kind: 'streamer', key };
}

/**
 * Resolve the backend WS base URL.
 *
 * Priority:
 *   1. Build-time env `VITE_BACKEND_WS_URL` (deploy pipeline sets this to
 *      `wss://api.thegamer.network` in prod).
 *   2. HTTPS page on `<sub>.<apex>` → `wss://api.<apex>` (covers the live
 *      <slug>.thegamer.network → api.thegamer.network swap without env vars).
 *   3. Dev fallback: `ws://<hostname>:<wsPort>`.
 *
 * Mirrors the per-game `resolveWsBase` so all browsers agree on the base host.
 */
export function resolveBackendWsBase(wsPort: number): string {
  const envUrl = (import.meta.env as Record<string, string | undefined>).VITE_BACKEND_WS_URL;
  if (envUrl) return envUrl.replace(/\/+$/, '');
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    const host = window.location.host;
    const apex = host.split('.').slice(-2).join('.');
    return `wss://api.${apex}`;
  }
  return `ws://${window.location.hostname}:${wsPort}`;
}

/**
 * Build the full WS URL for a given viewer mode.
 *
 *   streamer → `<base>/browser?key=<KEY>[&game=<slug>]`
 *   viewer   → `<base>/watch/<HANDLE>/feed`
 *
 * `extraQuery` is appended only in streamer mode — viewer-feed has no query
 * params and the server tolerates none.
 */
export function wsUrlFor(
  mode: ViewerMode,
  base: string,
  extraQuery?: Record<string, string>,
): string {
  if (mode.kind === 'viewer') {
    return `${base}/watch/${encodeURIComponent(mode.handle)}/feed`;
  }
  const params = new URLSearchParams();
  params.set('key', mode.key);
  if (extraQuery) {
    for (const [k, v] of Object.entries(extraQuery)) {
      if (v) params.set(k, v);
    }
  }
  return `${base}/browser?${params.toString()}`;
}

/** Convenience: `true` when this iframe is rendering for the public watch SPA. */
export function isViewerMode(mode: ViewerMode): boolean {
  return mode.kind === 'viewer';
}
